import { AuthenticatedLayout } from 'modules/layout/AuthenticatedLayout.tsx';
import { DefaultLayout } from 'modules/layout/DefaultLayout.tsx';
import { lazy, useContext, useMemo } from 'react';
import { createBrowserRouter, RouteObject, RouterProvider } from 'react-router-dom';
import { AuthContext } from 'shared/contexts/AuthContext.tsx';
import { RealtimerProvider } from '../realtimer/RealtimerProvider.tsx';
import { ServiceUnavailablePage } from './ServiceUnavailablePage.tsx';

const DashboardPage = lazy(() => import('./DashboardPage.tsx'));
const JobOrderDetailsPage = lazy(() => import('./JobOrderDetailsPage.tsx'));
const ArchivedJobOrderDetailsPage = lazy(() => import('./ArchivedJobOrderDetailsPage.tsx'));
const JobOrdersHistoryPage = lazy(() => import('./JobOrdersHistoryPage.tsx'));
const ComparerPage = lazy(() => import('./ComparerPage.tsx'));
const NotFoundPage = lazy(() => import('./NotFoundPage.tsx'));
const SignInPage = lazy(() => import('./SignInPage.tsx'));

const defaultRoutes: RouteObject[] = [
  {
    path: '404',
    element: <NotFoundPage />,
  },
  {
    path: '503',
    element: <ServiceUnavailablePage />,
  },
  {
    path: '/',
    element: <DefaultLayout />,
    errorElement: <NotFoundPage />,
    children: [
      { index: true, element: <NotFoundPage /> },
      {
        path: '/:dashboardId',
        element: <SignInPage />,
      },
    ],
  },
];
const authRoutes: RouteObject[] = [
  {
    path: '404',
    element: <NotFoundPage />,
  },
  {
    path: '503',
    element: <ServiceUnavailablePage />,
  },
  {
    path: '/',
    element: (
      <RealtimerProvider>
        <AuthenticatedLayout />
      </RealtimerProvider>
    ),
    errorElement: <NotFoundPage />,
    children: [
      {
        path: '/:dashboardId',
        children: [
          { index: true, element: <DashboardPage /> },
          {
            path: 'job-orders-history',
            element: <JobOrdersHistoryPage />,
          },
          {
            path: 'archived-job-order',
            children: [
              {
                path: ':jobOrderId',
                children: [
                  { index: true, element: <ArchivedJobOrderDetailsPage /> },
                  {
                    path: 'blinds-comparison',
                    element: <ComparerPage />,
                  },
                ],
              },
            ],
          },
          {
            path: 'job-order',
            children: [
              {
                path: ':jobOrderId',
                children: [
                  { index: true, element: <JobOrderDetailsPage /> },
                  {
                    path: 'blinds-comparison',
                    element: <ComparerPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const AppRouter = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const router = useMemo(
    () => createBrowserRouter(isAuthenticated ? authRoutes : defaultRoutes),
    [isAuthenticated],
  );
  return <RouterProvider router={router} />;
};
